import { Download, RotateCw, X } from "lucide-react";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { downloadCsv } from "@/shared/utils/csv";
import { colocaToast } from "@/shared/utils/toast";

import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import { useCryptoSwapOrders } from "../../hooks/useCryptoSwapOrders";
import { useSelectedCryptoSwap } from "../../hooks/useSelectedCryptoSwap";
import type { OtcOrder } from "../../types/cryptoSwaps.types";
import { buildCryptoSwapCsv, cryptoSwapCsvFilename } from "../../utils/cryptoSwapCsv";
import { ORDER_PARAM } from "../../utils/cryptoSwapLinks";
import { CryptoSwapOrderDrawer } from "./CryptoSwapOrderDrawer";
import { CryptoSwapOrdersCards } from "./CryptoSwapOrdersCards";
import { CryptoSwapOrdersFilters } from "./CryptoSwapOrdersFilters";
import { CryptoSwapOrdersTable } from "./CryptoSwapOrdersTable";
import { CryptoSwapStatusChips } from "./CryptoSwapStatusChips";

/** The purchases tab. The open order lives in `?order=`: opening PUSHES so back closes it, closing REPLACES. */
export function BuyHistoryPanel() {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const { isInternalUser } = useAuth();
  const orders = useCryptoSwapOrders();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedId = searchParams.get(ORDER_PARAM);
  const selected = useSelectedCryptoSwap(selectedId, orders.rows);

  const openOrder = useCallback(
    (order: OtcOrder) => {
      setSearchParams(
        (params) => {
          params.set(ORDER_PARAM, order.id);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeOrder = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(ORDER_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const { setFilter } = orders;
  const handleOrganizationChange = useCallback(
    (organizationId: string) => setFilter("organizationId", organizationId),
    [setFilter],
  );

  const handleExport = useCallback(() => {
    if (orders.rows.length === 0) {
      colocaToast.info(t("history_export_empty"));
      return;
    }
    const csv = buildCryptoSwapCsv(orders.rows, {
      columns: [
        t("csv_col_id"),
        t("csv_col_created_at"),
        t("csv_col_status"),
        t("csv_col_send_amount"),
        t("csv_col_send_currency"),
        t("csv_col_receive_amount"),
        t("csv_col_receive_currency"),
        t("csv_col_price"),
        t("csv_col_fee_conversion"),
        t("csv_col_fee_delivery"),
        t("csv_col_network"),
        t("csv_col_address"),
        t("csv_col_tx_hash"),
        t("csv_col_concept"),
        t("csv_col_paid_at"),
        t("csv_col_failed_at"),
      ],
      status: (order) => labels.status(order.status),
      network: (order) => labels.network(order.destination.network),
    });
    downloadCsv(csv, cryptoSwapCsvFilename(t("csv_filename")));
  }, [orders.rows, labels, t]);

  if (!orders.hasAccess) return <NoAccessView />;

  const pagination = (
    <ColocaPagination
      page={orders.page}
      perPage={orders.pageSize}
      total={orders.total}
      onPageChange={orders.setPage}
      withSeparator={false}
      summary={
        <span className="text-sm text-muted-foreground">
          {t(orders.total === 1 ? "history_count_one" : "history_count_other", { count: orders.total })}
        </span>
      }
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <CryptoSwapStatusChips
          value={orders.filters.status}
          onChange={(status) => setFilter("status", status)}
        />
        <ColocaButton
          variant="default"
          icon={Download}
          className="shrink-0 self-start"
          onClick={handleExport}
        >
          {t("history_export")}
        </ColocaButton>
      </div>

      {selected.notFound && selectedId && (
        <ColocaAlert
          variant="secondary"
          text={t("drawer_not_found")}
          controls={
            <ColocaIconButton
              variant="ghost"
              size="sm"
              icon={X}
              aria-label={t("drawer_not_found_dismiss")}
              onClick={closeOrder}
            />
          }
        />
      )}

      <CryptoSwapOrdersFilters
        filters={orders.filters}
        onRangeChange={orders.setRange}
        onOrganizationChange={isInternalUser ? handleOrganizationChange : undefined}
      />

      {orders.hasError && (
        <ColocaAlert
          variant="destructive"
          text={t("history_load_error")}
          actions={
            <ColocaButton variant="default" icon={RotateCw} onClick={orders.refresh}>
              {t("pairs_retry")}
            </ColocaButton>
          }
        />
      )}

      <div className="hidden lg:block">
        <CryptoSwapOrdersTable
          rows={orders.rows}
          isLoading={orders.isLoading}
          isFiltered={orders.isFiltered}
          onOpen={openOrder}
          footer={orders.total > orders.pageSize ? pagination : undefined}
        />
      </div>

      <div className="flex flex-col gap-4 lg:hidden">
        <CryptoSwapOrdersCards
          rows={orders.rows}
          isLoading={orders.isLoading}
          isFiltered={orders.isFiltered}
          onOpen={openOrder}
        />
        {orders.total > orders.pageSize && (
          <ColocaPagination
            page={orders.page}
            perPage={orders.pageSize}
            total={orders.total}
            onPageChange={orders.setPage}
          />
        )}
      </div>

      <CryptoSwapOrderDrawer
        order={selected.order}
        isLoading={selected.isLoading}
        open={!!selectedId && !selected.notFound}
        onOpenChange={(next) => {
          if (!next) closeOrder();
        }}
      />
    </div>
  );
}
